import type {AiPromptBox} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {AiPromptBoxSize, PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {PromptAttachmentType} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import type {
  ContextualMenuGroup,
  ContextualMenuItemWithIcon,
  ContextualMenuItemWithImage,
} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu/ai-prompt-box-contextual-menu.types';
import {
  ContextualMenuGroupType,
  ContextualMenuItemGraphicType,
} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu/ai-prompt-box-contextual-menu.types';
import {getAssetUrl} from '@Utils/s3.util';
import {isUserPremium} from '@Libraries/user.library';
import {AiVideoAspectRatio, AiVideoAudio, AiVideoCameraAngle, AiVideoFilterKey, AiVideoQuality, AiVideoTextSetting, LOADING_KEY_PREFIX} from '@Panels/ai-video-panel/ai-video-panel.types';

export const AI_VIDEOS_ASSET_PREFIX = 'poster-editor-thumbnails/ai-video/';
export const EMPTY_AI_VIDEO_INFOGRAPHICS = 'ai-video-empty-infographics';
export const EMPTY_AI_VIDEO_INFOGRAPHICS_ALL = 'ai-video-empty-infographics-all';
export const EMPTY_AI_VIDEO_INFOGRAPHICS_LANDSCAPE = 'ai-video-empty-infographics-landscape';
export const AI_VIDEO_GENERATION_LOADING_KEY = 'ai-video-generation';
export const MAX_CONCURRENT_AI_VIDEO_GENERATIONS = 5;
export const MAX_AI_VIDEO_REFERENCE_IMAGES = 3;

export const getLoadingKeyForAiVideoAddSelected = (panelId: string): string => {
  return `${LOADING_KEY_PREFIX}-add-selected-${panelId}`;
};

export const getAiVideoCreditCost = (quality: AiVideoQuality, audio: AiVideoAudio): number => {
  const is4K = quality === AiVideoQuality.UHD;
  const withAudio = audio === AiVideoAudio.YES;

  if (is4K) {
    return withAudio ? 27 : 23;
  }
  return withAudio ? 12 : 9;
};

export const DEFAULT_AI_VIDEO_QUALITY = AiVideoQuality.SD;
export const DEFAULT_AI_VIDEO_AUDIO = AiVideoAudio.YES;
export const DEFAULT_AI_VIDEO_ASPECT_RATIO = AiVideoAspectRatio.LANDSCAPE;
export const DEFAULT_AI_VIDEO_CAMERA_ANGLE = AiVideoCameraAngle.DEFAULT;

export const getAiVideoSkeletonDimensions = (aspectRatio: AiVideoAspectRatio): {width: number; height: number} => {
  if (aspectRatio === AiVideoAspectRatio.LANDSCAPE) {
    return {width: 1280, height: 720};
  }
  return {width: 720, height: 1280};
};

export const getContextualMenuGroups = (): ContextualMenuGroup[] => {
  return [
    {
      label: window.i18next.t('pmwjs_quality'),
      key: AiVideoFilterKey.QUALITY,
      type: ContextualMenuGroupType.PILLS,
      items: [
        {
          label: '4K',
          value: AiVideoQuality.UHD,
          active: false,
          description: window.i18next.t('pmwjs_ultra_hd'),
          graphicType: ContextualMenuItemGraphicType.ICON,
          icon: 'icon-ai-video-4k',
        } as ContextualMenuItemWithIcon,
        {
          label: '1080p',
          value: AiVideoQuality.HD,
          active: false,
          description: window.i18next.t('pmwjs_full_hd'),
          graphicType: ContextualMenuItemGraphicType.ICON,
          icon: 'icon-ai-video-fhd',
        } as ContextualMenuItemWithIcon,
        {
          label: '720p',
          value: AiVideoQuality.SD,
          active: true,
          description: window.i18next.t('pmwjs_hd'),
          graphicType: ContextualMenuItemGraphicType.ICON,
          icon: 'icon-ai-video-hd',
        } as ContextualMenuItemWithIcon,
      ],
    },
    {
      label: window.i18next.t('pmwjs_aspect_ratio'),
      key: AiVideoFilterKey.ASPECT_RATIO,
      type: ContextualMenuGroupType.PILLS,
      items: [
        {
          label: '9:16',
          value: AiVideoAspectRatio.PORTRAIT,
          active: false,
          description: window.i18next.t('pmwjs_vertical'),
          graphicType: ContextualMenuItemGraphicType.ICON,
          icon: 'icon-portrait',
        } as ContextualMenuItemWithIcon,
        {
          label: '16:9',
          value: AiVideoAspectRatio.LANDSCAPE,
          active: true,
          description: window.i18next.t('pmwjs_wide_screen'),
          graphicType: ContextualMenuItemGraphicType.ICON,
          icon: 'icon-landscape',
        } as ContextualMenuItemWithIcon,
      ],
    },
    {
      label: window.i18next.t('pmwjs_audio'),
      key: AiVideoFilterKey.AUDIO,
      type: ContextualMenuGroupType.PILLS,
      items: [
        {
          label: window.i18next.t('pmwjs_on'),
          value: AiVideoAudio.YES,
          active: true,
          graphicType: ContextualMenuItemGraphicType.ICON,
          icon: 'icon-sound',
        } as ContextualMenuItemWithIcon,
        {
          label: window.i18next.t('pmwjs_off'),
          value: AiVideoAudio.NO,
          active: false,
          graphicType: ContextualMenuItemGraphicType.ICON,
          icon: 'icon-sound-off',
        } as ContextualMenuItemWithIcon,
      ],
    },
    {
      label: window.i18next.t('pmwjs_camera_angles'),
      key: AiVideoFilterKey.CAMERA_ANGLE,
      type: ContextualMenuGroupType.CARDS,
      items: [
        {
          label: window.i18next.t('pmwjs_default'),
          value: AiVideoCameraAngle.DEFAULT,
          active: true,
          graphicType: ContextualMenuItemGraphicType.IMAGE,
          imageUrl: getAssetUrl(`${AI_VIDEOS_ASSET_PREFIX}default.png`),
        } as ContextualMenuItemWithImage,
        {
          label: window.i18next.t('pmwjs_top_view'),
          value: AiVideoCameraAngle.TOP_VIEW,
          active: false,
          graphicType: ContextualMenuItemGraphicType.IMAGE,
          imageUrl: getAssetUrl(`${AI_VIDEOS_ASSET_PREFIX}top-view.png?ts=1779356972`),
        } as ContextualMenuItemWithImage,
        {
          label: window.i18next.t('pmwjs_close_up'),
          value: AiVideoCameraAngle.CLOSE_UP,
          active: false,
          graphicType: ContextualMenuItemGraphicType.IMAGE,
          imageUrl: getAssetUrl(`${AI_VIDEOS_ASSET_PREFIX}close-up.png`),
        } as ContextualMenuItemWithImage,
        {
          label: window.i18next.t('pmwjs_wide_screen'),
          value: AiVideoCameraAngle.WIDE_SCREEN,
          active: false,
          graphicType: ContextualMenuItemGraphicType.IMAGE,
          imageUrl: getAssetUrl(`${AI_VIDEOS_ASSET_PREFIX}wide-screen.png`),
        } as ContextualMenuItemWithImage,
        {
          label: window.i18next.t('pmwjs_dramatic_angle'),
          value: AiVideoCameraAngle.DRAMATIC_ANGLE,
          active: false,
          graphicType: ContextualMenuItemGraphicType.IMAGE,
          imageUrl: getAssetUrl(`${AI_VIDEOS_ASSET_PREFIX}dramatic-angle.png`),
        } as ContextualMenuItemWithImage,
      ],
    },
    {
      label: window.i18next.t('pmwjs_text_settings'),
      key: AiVideoFilterKey.TEXT_SETTING,
      type: ContextualMenuGroupType.CARDS,
      items: [
        {
          label: window.i18next.t('pmwjs_on_screen_text'),
          value: AiVideoTextSetting.ON_SCREEN_TEXT,
          active: false,
          description: window.i18next.t('pmwjs_on_screen_text_description'),
          graphicType: ContextualMenuItemGraphicType.ICON,
          icon: 'icon-cc',
        } as ContextualMenuItemWithIcon,
        {
          label: window.i18next.t('pmwjs_character_speaks_it'),
          value: AiVideoTextSetting.CHARACTER_SPEAKS,
          active: false,
          description: window.i18next.t('pmwjs_character_speaks_it_description'),
          graphicType: ContextualMenuItemGraphicType.ICON,
          icon: 'icon-voice-profile',
        } as ContextualMenuItemWithIcon,
        {
          label: window.i18next.t('pmwjs_voiceover'),
          value: AiVideoTextSetting.VOICEOVER,
          active: false,
          description: window.i18next.t('pmwjs_voiceover_description'),
          graphicType: ContextualMenuItemGraphicType.ICON,
          icon: 'icon-sound',
        } as ContextualMenuItemWithIcon,
      ],
    },
  ];
};

export const getPromptBoxOptions = (remainingCredits: number): AiPromptBox => {
  return {
    placeholder: window.i18next.t('pmwjs_ai_video_prompt_placeholder'),
    showInitialAnimation: false,
    size: AiPromptBoxSize.SMALL_EXTENDED,
    creditsPill: {
      visible: true,
      actionCost: getAiVideoCreditCost(DEFAULT_AI_VIDEO_QUALITY, DEFAULT_AI_VIDEO_AUDIO),
      remainingCredits,
      showPremiumIcon: !isUserPremium(),
    },
    contextualMenu: getContextualMenuGroups(),
    attachments: {
      items: [],
      maxAttachments: MAX_AI_VIDEO_REFERENCE_IMAGES,
      dropdown: [PromptAttachmentType.UPLOAD_FROM_DEVICE, PromptAttachmentType.CAPTURE_PHOTO, PromptAttachmentType.IMAGE],
      pasteAsReferenceImages: true,
    },
    promptBoxState: PromptBoxState.ACTIVE,
    prompt: '',
  };
};
